/**
 * Configuração da rotação de painéis no Modo TV (playlist + minutos).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import './MonitoramentoTvRotationModal.css';

const MIN_INTERVAL = 1;
const MAX_INTERVAL = 60;
const DEFAULT_INTERVAL = 5;

function getAuthHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
}

function clampInterval(raw) {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return DEFAULT_INTERVAL;
  return Math.min(MAX_INTERVAL, Math.max(MIN_INTERVAL, n));
}

function normalizeIds(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  raw.forEach((item) => {
    const id = Number(item);
    if (!Number.isFinite(id) || id <= 0 || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

export function MonitoramentoTvRotationModal({
  open,
  onClose,
  onSaved,
  panels = [],
  initialRotation = null,
}) {
  const baseUrl = getApiBaseUrl();
  const [enabled, setEnabled] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState(DEFAULT_INTERVAL);
  const [panelIds, setPanelIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setEnabled(!!initialRotation?.enabled);
    setIntervalMinutes(clampInterval(initialRotation?.interval_minutes));
    setPanelIds(normalizeIds(initialRotation?.panel_ids));
    setError(null);
    setSaving(false);
  }, [open, initialRotation]);

  const panelById = useMemo(() => {
    const map = new Map();
    (panels || []).forEach((p) => {
      if (p && p.id != null) map.set(Number(p.id), p);
    });
    return map;
  }, [panels]);

  const playlist = useMemo(
    () => panelIds.filter((id) => panelById.has(id)),
    [panelIds, panelById]
  );

  const restPanels = useMemo(() => {
    const inPlay = new Set(playlist);
    return (panels || []).filter((p) => p && !inPlay.has(Number(p.id)));
  }, [panels, playlist]);

  const canEnable = playlist.length >= 2;

  const togglePanel = useCallback((id) => {
    const num = Number(id);
    setPanelIds((prev) => {
      if (prev.includes(num)) return prev.filter((x) => x !== num);
      return [...prev, num];
    });
  }, []);

  const movePanel = useCallback((id, dir) => {
    const num = Number(id);
    setPanelIds((prev) => {
      const list = prev.filter((x) => panelById.has(x));
      const idx = list.indexOf(num);
      if (idx < 0) return prev;
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= list.length) return list;
      const copy = [...list];
      const [item] = copy.splice(idx, 1);
      copy.splice(nextIdx, 0, item);
      return copy;
    });
  }, [panelById]);

  const handleToggleEnabled = () => {
    setError(null);
    setEnabled((prev) => !prev);
  };

  const handleSave = async () => {
    if (enabled && playlist.length < 2) {
      setError('Selecione pelo menos 2 painéis para habilitar.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/api/monitoramento/externo/tv-rotation`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          enabled: enabled && canEnable,
          interval_minutes: intervalMinutes,
          panel_ids: playlist,
        }),
      });
      const json = await res.json();
      if (json.success) {
        onSaved && onSaved(json.data);
        onClose();
      } else {
        setError(json.message || 'Erro ao salvar');
      }
    } catch (e) {
      setError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="mon-tv-rot-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mon-tv-rot-title"
    >
      <div className="mon-tv-rot-modal">
        <div className="mon-tv-rot-header">
          <h2 id="mon-tv-rot-title">Rotação no Modo TV</h2>
          <button type="button" className="mon-tv-rot-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>

        <div className="mon-tv-rot-body">
          {error ? (
            <div className="mon-tv-rot-error" role="alert">{error}</div>
          ) : null}

          <label className="mon-tv-rot-switch">
            <input
              type="checkbox"
              checked={enabled}
              disabled={saving}
              onChange={handleToggleEnabled}
            />
            <span className="mon-tv-rot-switch-ui" aria-hidden />
            <span className="mon-tv-rot-switch-label">Rotação no Modo TV</span>
          </label>
          {enabled && !canEnable ? (
            <p className="mon-tv-rot-hint mon-tv-rot-hint--warn">Selecione pelo menos 2 painéis para habilitar.</p>
          ) : (
            <p className="mon-tv-rot-hint">
              No Modo TV, os painéis marcados alternam na ordem abaixo.
            </p>
          )}

          <div className="mon-tv-rot-field">
            <label htmlFor="mon-tv-rot-interval">Trocar a cada (minutos)</label>
            <input
              id="mon-tv-rot-interval"
              type="number"
              min={MIN_INTERVAL}
              max={MAX_INTERVAL}
              value={intervalMinutes}
              onChange={(e) => setIntervalMinutes(clampInterval(e.target.value))}
              className="mon-tv-rot-input"
            />
            <span className="mon-tv-rot-hint-inline">1 a 60</span>
          </div>

          <div className="mon-tv-rot-field">
            <span className="mon-tv-rot-list-label">Ordem de passagem</span>
            <div className="mon-tv-rot-list">
              {playlist.length === 0 && restPanels.length === 0 ? (
                <div className="mon-tv-rot-empty">Nenhum painel cadastrado.</div>
              ) : null}
              {playlist.map((id, idx) => {
                const panel = panelById.get(id);
                if (!panel) return null;
                return (
                  <div key={id} className="mon-tv-rot-row is-on">
                    <label className="mon-tv-rot-row-main deq-check-label">
                      <input
                        type="checkbox"
                        className="deq-check-native"
                        checked
                        onChange={() => togglePanel(id)}
                      />
                      <span className="deq-check-box" aria-hidden="true" />
                      <span className="mon-tv-rot-ord">{idx + 1}</span>
                      <span className="mon-tv-rot-name">{panel.nome_painel}</span>
                    </label>
                    <div className="mon-tv-rot-move">
                      <button
                        type="button"
                        className="mon-tv-rot-move-btn"
                        disabled={idx === 0}
                        onClick={() => movePanel(id, -1)}
                        aria-label={`Subir ${panel.nome_painel}`}
                      >
                        <ChevronUp size={14} strokeWidth={2} />
                      </button>
                      <button
                        type="button"
                        className="mon-tv-rot-move-btn"
                        disabled={idx === playlist.length - 1}
                        onClick={() => movePanel(id, 1)}
                        aria-label={`Descer ${panel.nome_painel}`}
                      >
                        <ChevronDown size={14} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {restPanels.map((panel) => (
                <div key={panel.id} className="mon-tv-rot-row">
                  <label className="mon-tv-rot-row-main deq-check-label">
                    <input
                      type="checkbox"
                      className="deq-check-native"
                      checked={false}
                      onChange={() => togglePanel(panel.id)}
                    />
                    <span className="deq-check-box" aria-hidden="true" />
                    <span className="mon-tv-rot-name">{panel.nome_painel}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mon-tv-rot-footer">
          <button type="button" className="user-form-link-btn user-form-footer-btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
